/** Longer return URLs are rejected outright. */
export const MAX_RETURN_URL_LENGTH = 2048;

const parseUrl = (value: string): URL | undefined => {
  try {
    // No base URL: relative and scheme-relative values (/path, //host, /\host) fail to parse.
    return new URL(value);
  } catch {
    return undefined;
  }
};

const normaliseOrigins = (origins: readonly string[]): string[] =>
  origins.flatMap((origin) => parseUrl(origin)?.origin ?? []);

/** The parsed URL when `raw` is an absolute https: URL, at most MAX_RETURN_URL_LENGTH long, on an allowed origin. */
const parseAllowedUrl = (raw: string | null | undefined, allowedOrigins: readonly string[]): URL | undefined => {
  if (!raw || raw.length > MAX_RETURN_URL_LENGTH) {
    return undefined;
  }

  const url = parseUrl(raw);
  if (url?.protocol !== 'https:' || url.username || url.password) {
    return undefined;
  }

  return normaliseOrigins(allowedOrigins).includes(url.origin) ? url : undefined;
};

/**
 * Whether `raw` is an absolute https: URL whose normalised origin exactly matches one of `allowedOrigins`.
 * Host case and a default :443 port are normalised away; anything else about the origin must match.
 */
export const isAllowedReturnUrl = (raw: string | null | undefined, allowedOrigins: readonly string[]): boolean =>
  parseAllowedUrl(raw, allowedOrigins) !== undefined;

/**
 * Where to send a player back to: `raw`, normalised, when it is allowed (see isAllowedReturnUrl), otherwise
 * `fallback`. Path, query and hash are kept.
 */
export const resolveReturnUrl = (
  raw: string | null | undefined,
  allowedOrigins: readonly string[],
  fallback: string,
): string => parseAllowedUrl(raw, allowedOrigins)?.href ?? fallback;
